/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/require-default-props */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
    Query,
    Builder,
    BasicConfig,
    Utils as QbUtils,
} from 'react-awesome-query-builder';
import PropTypes from 'prop-types';
import GeneralPreloader from 'components/app/common/preloader/generalPreloader'; // eslint-disable-line max-len
import './queryBuilder.scss';
import { useQueryBuilderRaqbFields } from './useQueryBuilderRaqbFields';
import { safeStringify } from './queryBuilderUtils';
import { normalizeQueryTreeForRaqb } from './normalizeQueryTreeForRaqb';
import DatetimeTzWidget from './DatetimeTzWidget';

/* eslint-disable react/no-array-index-key, react/prop-types */
function SelectWithoutBlank({ listValues, value, setValue, readonly }) {
    const items = useMemo(() => listValues || [], [listValues]);
    const itemsKey = useMemo(
        () => items.map((i) => String(i.value)).join('\0'),
        [items]
    );
    /** Avoid infinite setValue if RAQB lags updating `value` */
    const defaultAppliedForListRef = useRef(false);

    useEffect(() => {
        defaultAppliedForListRef.current = false;
    }, [itemsKey]);

    useEffect(() => {
        if (items.length === 0) return;
        if (value != null && value !== '') {
            defaultAppliedForListRef.current = false;
            return;
        }
        if (defaultAppliedForListRef.current) return;
        defaultAppliedForListRef.current = true;
        setValue(items[0].value);
    }, [value, items, setValue]);

    return (
        <select
            value={value ?? ''}
            disabled={readonly}
            onChange={(e) => setValue(e.target.value)}
        >
            {items.map((item, i) => (
                <option key={`${item.value}-${i}`} value={item.value}>
                    {item.title || item.value}
                </option>
            ))}
        </select>
    );
}

const DEFAULT_RULE = (field = 'firstname') => ({
    id: QbUtils.uuid(),
    type: 'group',
    children1: {
        [QbUtils.uuid()]: {
            type: 'rule',
            properties: {
                default: true,
                field,
                operator: 'equal',
                value: [null],
                valueSrc: ['value'],
                valueType: ['text'],
            },
        },
    },
    properties: { conjunction: 'AND', not: false },
});

function QueryBuilder({
    onChange,
    value,
    fields,
    projectId,
    enableDefaultRuleset = false,
    defaultFirstField = 'firstname',
    withoutOrConjunction = false,
    excludeCheckinFields = false,
    className,
}) {
    const { fieldConfig, loading } = useQueryBuilderRaqbFields(
        fields,
        projectId,
        { excludeCheckinFields }
    );

    const conjunctionOverride = useMemo(
        () =>
            withoutOrConjunction
                ? {
                      conjunctions: {
                          AND: {
                              ...BasicConfig.conjunctions.AND,
                              label: 'AND',
                          },
                      },
                  }
                : {},
        [withoutOrConjunction]
    );

    const defaultConfig = useMemo(
        () => ({
            ...BasicConfig,
            fields: fieldConfig,
            widgets: {
                ...BasicConfig.widgets,
                select: {
                    ...BasicConfig.widgets.select,
                    factory: (props) => <SelectWithoutBlank {...props} />,
                },
                datetime: {
                    ...BasicConfig.widgets.datetime,
                    factory: (props) => <DatetimeTzWidget {...props} />,
                },
            },
            types: {
                ...BasicConfig.types,
                select: {
                    ...BasicConfig.types.select,
                    defaultOperator: 'equal',
                    widgets: {
                        select: {
                            operators: ['equal', 'not_equal'],
                        },
                    },
                },
                datetime: {
                    ...BasicConfig.types.datetime,
                    defaultOperator: 'greater_or_equal',
                    widgets: {
                        ...BasicConfig.types.datetime.widgets,
                        datetime: {
                            ...BasicConfig.types.datetime.widgets.datetime,
                            operators: [
                                'not_equal',
                                'less',
                                'less_or_equal',
                                'greater',
                                'greater_or_equal',
                                'between',
                                'not_between',
                                'is_empty',
                                'is_not_empty',
                            ],
                        },
                    },
                },
            },
            settings: {
                ...BasicConfig.settings,
                showNot: false,
            },
            ...conjunctionOverride,
        }),
        [fieldConfig, conjunctionOverride]
    );

    const emptyRuleByFieldRef = useRef({});
    const emptyGroupRef = useRef(null);
    const externalValueJsonRef = useRef(null);
    const externalValueRef = useRef(value);
    /** Stops onChange echo: internal edit should not re-sync from value prop */
    const lastEmittedTreeJsonRef = useRef(null);

    const queryValue = useMemo(() => {
        const isEmptyValue = value == null || Object.keys(value).length === 0;

        if (isEmptyValue) {
            if (enableDefaultRuleset) {
                const k = defaultFirstField;
                if (!emptyRuleByFieldRef.current[k]) {
                    emptyRuleByFieldRef.current[k] = DEFAULT_RULE(k);
                }
                return emptyRuleByFieldRef.current[k];
            }
            if (!emptyGroupRef.current) {
                emptyGroupRef.current = {
                    id: QbUtils.uuid(),
                    type: 'group',
                    children1: {},
                    properties: { conjunction: 'AND', not: false },
                };
            }
            return emptyGroupRef.current;
        }

        const j = safeStringify(value);
        if (externalValueJsonRef.current === j) {
            return externalValueRef.current;
        }
        externalValueJsonRef.current = j;
        externalValueRef.current = value;
        return value;
    }, [value, enableDefaultRuleset, defaultFirstField]);

    const [tree, setTree] = useState(null);

    useEffect(() => {
        if (loading) return;
        const normalized = normalizeQueryTreeForRaqb(queryValue, defaultConfig); // eslint-disable-line max-len
        const newTree = QbUtils.checkTree(
            QbUtils.loadTree(normalized),
            defaultConfig
        );
        const newJson = safeStringify(QbUtils.getTree(newTree)); // eslint-disable-line max-len
        if (
            lastEmittedTreeJsonRef.current != null &&
            lastEmittedTreeJsonRef.current === newJson
        ) {
            lastEmittedTreeJsonRef.current = null;
            return;
        }
        setTree((prev) => {
            if (!prev) return newTree;
            try {
                const prevJson = safeStringify(QbUtils.getTree(prev)); // eslint-disable-line max-len
                if (prevJson === newJson) {
                    return prev;
                }
            } catch {
                /* noop */
            }
            return newTree;
        });
    }, [queryValue, defaultConfig, loading]);

    const handleChange = (immutableTree) => {
        const jsonTree = QbUtils.getTree(immutableTree);
        const normalized = normalizeQueryTreeForRaqb(jsonTree, defaultConfig); // eslint-disable-line max-len
        const nextTree = QbUtils.checkTree(
            QbUtils.loadTree(normalized),
            defaultConfig
        );
        const payload = QbUtils.getTree(nextTree);
        lastEmittedTreeJsonRef.current = safeStringify(payload); // eslint-disable-line max-len
        setTree(nextTree);
        if (onChange) {
            onChange(payload);
        }
    };

    const renderBuilder = (builderProps) => (
        <div className="query-builder-container">
            <div className="query-builder qb-lite">
                <Builder {...builderProps} />
            </div>
        </div>
    );

    if (loading || !tree) {
        return (
            <div className="tw-flex tw-w-full">
                <GeneralPreloader />
            </div>
        );
    }

    return (
        <div className={className || 'tw-flex tw-w-full'}>
            <Query
                {...defaultConfig}
                value={tree}
                onChange={handleChange}
                renderBuilder={renderBuilder}
            />
        </div>
    );
}

QueryBuilder.propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.object,
    fields: PropTypes.object,
    projectId: PropTypes.string,
    enableDefaultRuleset: PropTypes.bool,
    defaultFirstField: PropTypes.string,
    withoutOrConjunction: PropTypes.bool,
    excludeCheckinFields: PropTypes.bool,
    className: PropTypes.string,
};

export default QueryBuilder;
